import React, { useState, useEffect } from "react";
import { ref, set, update } from "firebase/database";
import { auth, db } from "../firebase";
import useFile from "../hooks/useFile";
import Popup from "./Popup";

const GameForm = ({ selectedGame = null, onClose, onSuccess }) => {
  const isEditMode = selectedGame !== null;
  const [game, setGame] = useState({
    gameName: "",
    organizerName: "",
    description: "",
    date: "",
    location: "",
    banner: "",
    fileId: null,
  });
  const { uploadImage, uploading, clearError, error: uploadError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [popup, setPopup] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load selected game data when in edit mode
  useEffect(() => {
    if (isEditMode && selectedGame) {
      setGame({
        gameName: selectedGame.gameName || "",
        organizerName: selectedGame.organizerName || "",
        description: selectedGame.description || "",
        date: selectedGame.date || "",
        location: selectedGame.location || "",
        banner: selectedGame.banner || "",
        fileId: selectedGame.file || null,
      });
      if (selectedGame.banner) {
        setPreview(selectedGame.banner);
      }
    }
  }, [isEditMode, selectedGame]);

  const handleChange = (f, v) => {
    setGame({ ...game, [f]: v });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      setPreview(null);
      setGame({ ...game, banner: "", fileId: null });
      return;
    }

    // If there's an existing uploaded file, delete it first
    if (game.fileId) {
      await deleteFile(game.fileId);
    }

    // Create preview first
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);

    // Upload immediately when file is selected
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      setGame({
        ...game,
        banner: result.fileUrl,
        fileId: result.fileId,
      });
    } else {
      setPopup({
        isOpen: true,
        message: result.error || "Failed to upload banner",
        type: "error",
      });
      setPreview(null);
      e.target.value = "";
    }
  };

  // Generate unique game ID
  const generateGameId = () => {
    return "AUC-" + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploading) {
      setPopup({
        isOpen: true,
        message: "Please wait for banner upload to complete",
        type: "info",
      });
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      if (isEditMode) {
        // Update existing game
        await update(ref(db, `games/${selectedGame.id}`), {
          updatedAt: Date.now(),
        });

        await update(ref(db, `games/${selectedGame.id}/details`), {
          title: game.gameName,
          organizerName: game.organizerName,
          description: game.description,
          date: game.date,
          location: game.location,
          banner: game.banner || "",
          file: game.fileId || "",
        });

        // Update user's games list reference
        await update(ref(db, `users/${user.uid}/games/${selectedGame.id}`), {
          gameName: game.gameName,
          updatedAt: Date.now(),
        });

        setPopup({
          isOpen: true,
          message: "Game updated successfully!",
          type: "success",
        });

        setTimeout(() => {
          if (onSuccess) onSuccess();
          if (onClose) onClose();
        }, 1500);
      } else {
        // Create new game
        const finalGameId = generateGameId();

        const gameData = {
          organizerUid: user.uid,
          createdAt: Date.now(),
          updatedAt: Date.now(),

          details: {
            title: game.gameName,
            organizerName: game.organizerName,
            status: "not_started",
            description: game.description,
            date: game.date,
            location: game.location,
            banner: game.banner || "",
            file: game.fileId || "",
            minBidIncrement: 1000,
          },

          teams: {},
          players: {},
          currentPlayer: null,
          currentBids: null,
          auctionSettings: {},
        };

        await set(ref(db, `games/${finalGameId}`), gameData);

        await set(ref(db, `users/${user.uid}/games/${finalGameId}`), {
          gameId: finalGameId,
          gameName: game.gameName,
          createdAt: Date.now(),
          status: "not_started",
        });

        setPopup({
          isOpen: true,
          message: "Game created successfully!",
          type: "success",
        });

        setTimeout(() => {
          if (onSuccess) onSuccess();
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      setPopup({
        isOpen: true,
        message: `Failed to ${isEditMode ? 'update' : 'create'} game: ${error.message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
      <div className="card-header bg-gradient text-white py-3" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0 fw-bold">
            <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
            {isEditMode ? 'Edit Game' : 'Create New Game'}
          </h4>
          {onClose && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={onClose}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-4">
        <form onSubmit={handleSubmit}>
          {/* Banner Upload */}
          <div className="mb-4 text-center">
            <div className="banner-upload mb-3">
              {preview ? (
                <div className="banner-preview">
                  <img
                    src={preview}
                    alt="Game banner preview"
                    className="rounded"
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger remove-banner"
                    onClick={async () => {
                      if (game.fileId) {
                        try {
                          setDeleting(true);
                          await deleteFile(game.fileId);
                        } catch (e) {
                        } finally {
                          setDeleting(false);
                        }
                      }
                      setPreview(null);
                      setGame({ ...game, banner: "", fileId: null });
                      const input = document.getElementById("bannerUpload");
                      if (input) input.value = "";
                    }}
                    disabled={uploading || loading || deleting}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>
              ) : (
                <div className="banner-placeholder">
                  {uploading || deleting ? (
                    <>
                      <div className="spinner-border text-primary mb-2" role="status">
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="mb-0">Uploading banner...</p>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-image"></i>
                      <p className="mb-0">Game Banner</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              type="file"
              id="bannerUpload"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading || loading || deleting}
              className="d-none"
            />
            <label
              htmlFor="bannerUpload"
              className={`btn btn-sm ${uploading || deleting ? "btn-secondary" : "btn-outline-primary"}`}
            >
              {uploading || deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-2"></i>
                  {preview ? "Change Banner" : "Upload Banner"}
                </>
              )}
            </label>
            <small className="d-block text-muted mt-1">Optional - Max 500KB (Recommended: 1200x400px)</small>
            {uploadError && (
              <small className="d-block text-danger mt-1">
                <i className="bi bi-exclamation-circle me-1"></i>
                Please try again after some time.
              </small>
            )}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-trophy-fill me-2 text-primary"></i>
                Game Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., IPL Auction 2025"
                required
                value={game.gameName}
                onChange={(e) => handleChange("gameName", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-person-badge-fill me-2 text-primary"></i>
                Organizer Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., John Doe"
                required
                value={game.organizerName}
                onChange={(e) => handleChange("organizerName", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-calendar-event me-2 text-primary"></i>
                Auction Date
              </label>
              <input
                type="date"
                className="form-control"
                value={game.date}
                onChange={(e) => handleChange("date", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                Location
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Dhaka, Bangladesh"
                value={game.location}
                onChange={(e) => handleChange("location", e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                <i className="bi bi-file-text-fill me-2 text-primary"></i>
                Description
              </label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Brief description about the auction..."
                value={game.description}
                onChange={(e) => handleChange("description", e.target.value)}
                disabled={loading}
              ></textarea>
            </div>

            {!isEditMode && (
              <div className="col-12">
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Note:</strong> After creating the game, configure teams, players, and auction settings from the dashboard.
                </div>
              </div>
            )}

            <div className="col-12 mt-4">
              <button 
                type="submit" 
                className="btn btn-success w-100 py-2 fw-semibold" 
                disabled={loading || uploading}
                style={{ fontSize: "1.05rem" }}
              >
                {loading || uploading || deleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {uploading ? "Uploading..." : isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : isEditMode ? (
                  <>
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Update Game
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle-fill me-2"></i>
                    Create Game
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: '', type: 'info' })}
        message={popup.message}
        type={popup.type}
      />

      <style jsx>{`
        .banner-upload {
          position: relative;
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
        }

        .banner-placeholder {
          width: 100%;
          height: 200px;
          border: 2px dashed #dee2e6;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .banner-placeholder:hover {
          border-color: #667eea;
          background: #f0f2ff;
        }

        .banner-placeholder i {
          font-size: 3rem;
          color: #6c757d;
          margin-bottom: 10px;
        }

        .banner-preview {
          position: relative;
          width: 100%;
        }

        .banner-preview img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          border: 3px solid #667eea;
          box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);
        }

        .remove-banner {
          position: absolute;
          top: 10px;
          right: 10px;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        .bg-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default GameForm;
